import { ARDU_COPTER_MODES, ARDU_PLANE_MODES } from '@/lib/mqtt/legacy/mavlink';
/**
 * `v1/output/aircraft/{sn}/telemetry` — airframe state + operational volume.
 *
 * Replaces the fields the legacy `networkid/{sn}` payload used to carry
 * inline (battery, armed, alt_asl, mission metadata) plus the two topics
 * that went dark in the cut-over: `traffic_control` (drone status) and
 * `TISB Stream/` (TIS-B conspicuity).
 *
 * Shape note: this is the *interim* NID payload (snake_case), not the
 * canonical camelCase schema in the FLYsafe MQTT doc. Both are accepted —
 * every rule reads the interim name first and falls back to the canonical
 * one — so the eventual backend correction is a no-op for us.
 *
 * This topic never creates a drone. It produces partials that merge onto
 * the baseline `remoteId.ts` builds, so a telemetry message arriving
 * before the first networkid message is held rather than fabricating an
 * identity-less aircraft.
 */
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';
import { partnerTenantFromPayload } from '@/lib/tenancy';
import {
  type FieldRule,
  accuracyMetres,
  applyRules,
  asBool,
  asNumber,
  asText,
  isoUtc,
  recordAccepted,
  recordParseFailure,
  traceId,
} from './registry';
import { readOiAltitudeBand } from '@/lib/mqtt/oiAltitude';

const TOPIC_KIND = 'telemetry';

/**
 * MAV_LANDED_STATE → label. The wire sends the 0-4 integer (EXTENDED_SYS_STATE);
 * a decoded string is passed through. 0 is UNDEFINED, i.e. "no information", so
 * it maps to null rather than to a state the aircraft is not known to be in.
 */
const LANDED_STATES: Record<number, string> = {
  1: 'On ground',
  2: 'In air',
  3: 'Takeoff',
  4: 'Landing',
};
/** Accepts the number (or a numeric string) and the labels / enum names a decoded
 *  publisher might send ("In air", "IN_AIR"); anything else is not a state we know. */
function landedState(v: unknown): string | null {
  const n = asNumber(v);
  if (n != null) return LANDED_STATES[n] ?? null;
  const text = asText(v);
  if (!text) return null;
  const key = text.toLowerCase().replace(/[\s_-]+/g, ' ');
  return Object.values(LANDED_STATES).find((label) => label.toLowerCase() === key) ?? null;
}

/** DJI / FH2 mode label. The 30 s silence watchdog publishes `mode: "Not connected"`
 *  for every source; that is connection state (`adapter_connected`), not a flight mode,
 *  and must not replace a real one. */
function modeLabel(v: unknown): string | null {
  const t = asText(v);
  return t && t.toLowerCase() !== 'not connected' ? t : null;
}

/** `-1` is the publisher's "no battery sensor" sentinel, not a reading. */
function batteryPct(v: unknown): number | null {
  const n = asNumber(v);
  if (n == null || n < 0) return null;
  return n > 100 ? 100 : n;
}

/**
 * ArduPilot `custom_mode` → mode name.
 *
 * The same integer means different modes on different airframes:
 * `custom_mode: 10` is AUTO on ArduPlane but undefined on ArduCopter. The
 * telemetry payload carries `autopilot` (MAV_AUTOPILOT) but not the frame
 * `type`, so the frame has to come from the drone's existing identity —
 * `uaType` on the networkid topic, which resolves to "Fixed-wing" for the
 * sandbox fleet. Without that hint SIMTL70/80 render as "MODE 10".
 *
 * The raw integer is kept on `_meta.rawFlightMode` either way, so a wrong
 * table never destroys the original value.
 *
 * When the MAVLink stream is also subscribed it claims `flight_mode` and
 * wins outright — it carries the real HEARTBEAT frame type.
 */
function flightModeFromCustom(
  v: unknown,
  payload: Record<string, unknown>,
  droneType?: string | null,
): string | null {
  // Canonical shape ships a decoded string — prefer it outright.
  const canonical = asText(payload.flightMode);
  if (canonical) return canonical;
  const mode = asNumber(v);
  if (mode == null) return null;
  const frame = (droneType ?? '').toLowerCase();
  const isPlane = asNumber(payload.type) === 1 || frame.includes('wing') || frame.includes('plane');
  const table = isPlane ? ARDU_PLANE_MODES : ARDU_COPTER_MODES;
  return table[mode] ?? `MODE ${mode}`;
}

/** MAV_AUTOPILOT → the envelope's coarse autopilot family. */
function apTypeFrom(
  payload: Record<string, unknown>,
): NonNullable<TelemetryEnvelope['_meta']>['apType'] {
  const source = asText(payload.source)?.toLowerCase() ?? '';
  if (source.includes('dji') || source.includes('fh2')) return 'dji';
  const ap = asNumber(payload.autopilot);
  if (ap === 3) return 'ardupilot'; // MAV_AUTOPILOT_ARDUPILOTMEGA
  if (ap === 12) return 'px4'; // MAV_AUTOPILOT_PX4
  return 'custom';
}

/** Read the first present of several aliases. Lets one rule cover both
 *  the interim and canonical field names. */
function firstOf(payload: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    const v = payload[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

/** `start_datetime` → elapsed flight time as "HH:MM".
 *
 *  The envelope has a `flight_time` slot that nothing has ever filled: the
 *  broker publishes the mission's start instant, not a duration. Recomputed
 *  on every message, so it ticks. Guards against a start in the future (clock
 *  skew) and an absurd elapsed value rather than rendering "-1:23"/"999:99". */
function flightTimeFrom(startIso: unknown): string | null {
  const s = asText(startIso);
  if (!s) return null;
  const start = Date.parse(s);
  if (Number.isNaN(start)) return null;
  const mins = Math.floor((Date.now() - start) / 60_000);
  if (mins < 0 || mins > 60 * 24 * 7) return null;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

const IGNORED = [
  // Identity — used to route the partial, not to populate it.
  'aircraft_serial',
  'sn',
  // Duplicates of networkid fields; networkid is authoritative for these
  // (it is the F3411 source of truth for position and velocity).
  'lat',
  'lon',
  'geodetic_altitude',
  'horizontal_speed',
  'vertical_speed',
  'horizontal_accuracy',
  'vertical_accuracy',
  'speed_accuracy',
  'timestamp_accuracy',
  // The same values again under other spellings — the live publisher sends `hAcc` / `hacc`
  // and `vAcc` / `vacc` next to `horizontal_accuracy` / `vertical_accuracy`, and `groundSpeed`
  // next to `horizontal_speed` (confirmed on the sandbox capture of 2026-10-08).
  'hAcc',
  'hacc',
  'vAcc',
  'vacc',
  'groundSpeed',
  // Identity / provenance already taken from networkid (`uaType`, `trackOrigin`); the DD
  // sources may repeat them on this topic under the doc's names.
  'UAS_TYPE',
  'track_origin_type',
  // UTM / OI correlation ids; the OI side reads them from `oi/declaration`.
  'utm_declaration_id',
  'declaration_id',
  // Its GEOMETRY is deliberately not consumed: the ticket maps the OI / flight
  // plan to `v1/output/aircraft/{sn}/oi/declaration`, and the FLYsafe MQTT doc
  // puts `oiPolygon[]` there and nowhere else. `operational_volume` rides along
  // on this topic too, but sourcing the OI polygon from it is not what either
  // document specifies.
  // Its DECLARED ALTITUDES are consumed — by `outputTelemetryToOiUpdate` below,
  // not by this envelope. `oi/declaration`'s copy carries only time_start /
  // time_end (verified live 2026-08-24), so this topic is the sole publisher of
  // a volume band with an explicit datum.
  'operational_volume',
  'flight_guid',
  // Backend correlation ids with no consumer. Retained on the raw
  // snapshot; listed here so they don't dominate the unmapped report.
  'internal_guid',
  'message_type',
  'complete',
] as const;

const RULES: readonly FieldRule<TelemetryEnvelope>[] = [
  { from: 'battery', to: 'battery_pct', via: batteryPct },
  { from: 'alt_asl', to: 'asl', via: asNumber },
  { from: 'pressure_altitude', to: 'pressure_altitude', via: asNumber },
  { from: 'armed', to: 'armed', via: asBool },
  // The doc's output names for values networkid normally carries. A source that puts them on
  // this topic instead (DD sources) is read in the post-pass below.
  { from: 'alt_agl', via: () => undefined },
  { from: 'heading', via: () => undefined },
  { from: 'landed_state', to: 'landed_state', via: landedState },
  { from: 'link_rssi_dbm', to: 'link_rssi_dbm', via: asNumber },
  // `flight_mode` has two sources. DJI / FH2 publish a label as `mode` (from `mode_code`),
  // mapped by the rule below. MAVLink's `custom_mode` / `flightMode` need the caller's
  // frame hint, so they are decoded after the rule pass — and win when both are present.
  // `_meta` is built there too.
  { from: 'custom_mode', via: () => undefined },
  { from: 'flightMode', via: () => undefined },
  { from: 'mode', to: 'flight_mode', via: modeLabel },
  { from: 'adapter_connected', to: 'adapter_connected', via: asBool },
  { from: 'source', to: 'source_name', via: asText },
  { from: 'tenant_id', to: 'tenant_id', via: asText },
  { from: 'tenantId', to: 'tenant_id', via: asText },
  // Cross-tenant provenance. Lives inside `ext` on the wire (verified
  // against the live sandbox broker) — see lib/tenancy.ts. Consumed by
  // `partnerTenantFromPayload` below, which reads it together with our own
  // configured tenant rather than field by field.
  { from: 'ext.sourceTenant', via: () => undefined },
  { from: 'sourceTenant', via: () => undefined },
  { from: 'source_tenant', via: () => undefined },
  { from: 'start_datetime', to: 'flight_time', via: flightTimeFrom },
  { from: 'end_datetime', via: () => undefined },
  // Mission metadata — consumed by `outputTelemetryToOiUpdate` onto the OI
  // envelope, not this one. `oi/declaration` doesn't carry them.
  { from: 'mission_main_flight_type', via: () => undefined },
  { from: 'legal_profile', via: () => undefined },
  { from: 'mission_notes', via: () => undefined },
  // Same — the OI volume band goes onto the OI envelope. Declared here so they
  // leave the unmapped report; they were live on the wire and unread until
  // 2026-08-24, while the OI panel showed waypoint AGL labelled "WGS84".
  { from: 'alt_lower_wgs84_m', via: () => undefined },
  { from: 'alt_upper_wgs84_m', via: () => undefined },
  { from: 'timestamp_delay', to: 'pipeline_delay_s', via: asNumber },
  { from: 'sequence', to: 'sequence', via: asNumber },
  { from: 'schema_version', to: 'schema_version', via: asText },
  { from: 'traceparent', to: 'trace_id', via: traceId },
  { from: 'timestamp_utc', to: 'received_at', via: isoUtc },
  {
    from: 'drone_status',
    to: 'status_label',
    via: asText,
    note: 'the field traffic_control used to supply; empty from aircraft_agent',
  },
  { from: 'model', to: 'drone_model', via: asText },
  { from: 'drone_type', to: 'drone_type', via: asText },
  // Consumed for provenance only — `autopilot` refines apType, and
  // takeoff/landing feed the OI mission block once populated.
  { from: 'autopilot', via: () => undefined },
  { from: 'takeoff', via: () => undefined },
  { from: 'landing', via: () => undefined },
  { from: 'pilot_id', via: () => undefined },
  { from: 'drone_guid', via: () => undefined },
  { from: 'notes', via: () => undefined },
  { from: 'tisb_rebroadcast_enabled', via: () => undefined },
  { from: 'share_telemetry_with_TISB', via: () => undefined },
];

export interface OutputTelemetryUpdate {
  droneId: string;
  partial: Partial<TelemetryEnvelope>;
  /** Fields present on the wire that no rule covers. Surfaced by the
   *  debug panel so a backend addition is visible, never silent. */
  unmapped: string[];
}

export function outputTelemetryToPartial(
  raw: unknown,
  /** Airframe of the drone as already known from `networkid.uaType`.
   *  Disambiguates the ArduPilot mode table — see `flightModeFromCustom`. */
  droneType?: string | null,
): OutputTelemetryUpdate | null {
  if (raw == null || typeof raw !== 'object') {
    recordParseFailure(TOPIC_KIND, 'payload is not an object');
    return null;
  }
  const p = raw as Record<string, unknown>;
  const droneId = asText(firstOf(p, 'aircraft_serial', 'sn', 'droneId'));
  if (!droneId) {
    recordParseFailure(TOPIC_KIND, 'missing aircraft_serial / sn');
    return null;
  }

  const { mapped, unmapped } = applyRules<TelemetryEnvelope>(TOPIC_KIND, p, RULES, IGNORED);

  const partial: Partial<TelemetryEnvelope> = { ...mapped };

  // Cross-tenant provenance, set positively only. `networkid` is the
  // identity topic and normally declares this first, but the connector
  // stamps `sourceTenant` on every partner topic, so honouring it here too
  // means a partner aircraft is classified from whichever message lands
  // first. Never written as 'internal': that would let one message without
  // `sourceTenant` reclassify an already-known partner as our own.
  const partnerTenant = partnerTenantFromPayload(p);
  if (partnerTenant) {
    partial.origin = 'partner';
    partial.partner_tenant = partnerTenant;
  }

  const mode = flightModeFromCustom(p.custom_mode, p, droneType);
  if (mode) partial.flight_mode = mode;

  // Provenance. `rawFlightMode` keeps the undecoded integer so a wrong
  // mode table is always recoverable from the envelope itself.
  const rawMode = asNumber(p.custom_mode);
  partial._meta = {
    apType: apTypeFrom(p),
    ...(rawMode != null ? { rawFlightMode: rawMode } : {}),
  };

  // `status_label` without a `status` leaves the Surveillance tile with a
  // label and no tone. Only set the enum when the publisher gives us a
  // value we recognise; an empty string must not become a fake 'Active'.
  const statusLabel = asText(p.drone_status);
  if (statusLabel) {
    const canonical: Record<string, NonNullable<TelemetryEnvelope['status']>> = {
      ACTIVE: 'Active',
      DAA: 'DAA',
      'DAA-DEG': 'DAA-DEG',
      SA: 'SA',
      LOST: 'LOST',
    };
    const match = canonical[statusLabel.toUpperCase()];
    if (match) partial.status = match;
  }

  // AGL is not on this topic at all (only networkid's `height`), so don't
  // synthesise one. ASL is, and is preferred over networkid's
  // geodeticAltitude only because the two agree to ~0.1 m and this one is
  // the barometric-corrected value.
  const aslM = asNumber(p.alt_asl);
  if (aslM != null) partial.asl = aslM;

  // `alt_agl` (metres, explicit AGL) is on this topic only for sources that publish it here;
  // when present it fills the AGL slots, as networkid's `height` does.
  const aglM = asNumber(p.alt_agl);
  if (aglM != null) {
    partial.aglM = aglM;
    partial.agl = aglM * 3.28084;
  }
  // Same for `heading`: normalised to 0-360 (DJI sends -180..180) and flagged as a real heading.
  const heading = asNumber(p.heading);
  if (heading != null && heading !== 361) {
    partial.heading = ((heading % 360) + 360) % 360;
    partial.heading_known = true;
  }

  // Accuracy fields duplicate networkid's, but if a publisher ever ships
  // telemetry without a networkid pair we still want a GPS reading.
  const hAcc = accuracyMetres(p.horizontal_accuracy);
  if (hAcc != null && partial.gps_hdop == null) partial.gps_hdop = hAcc / 5;

  recordAccepted(TOPIC_KIND);
  return { droneId, partial, unmapped };
}

/* ------------------------------------------------------------------ */
/*  C2 — TIS-B conspicuity + agent link                                */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  OI decoration — mission metadata that rides on telemetry            */
/* ------------------------------------------------------------------ */

export interface OutputTelemetryOiUpdate {
  drone_id: string;
  partial: {
    op_type?: string | null;
    legal_rule?: string | null;
    mission_notes?: string | null;
    altitude_floor_m?: number | null;
    altitude_ceiling_m?: number | null;
    altitude_datum?: string | null;
  };
}

/**
 * Mission metadata for the OI envelope.
 *
 * `flightplan.ts` looks for `main_flight_type` / `legal_profile` on the
 * `oi/declaration` payload, but that topic carries neither — verified against
 * all three tenant brokers, where its keys are only UAS_ID / UAS_ID_TYPE /
 * declaration_id / sn / timestamp / waypoints / flight_volume /
 * operational_volume / ext. The fields exist on `telemetry` instead, which is
 * why the OI panel's "Operation Type" and "Legal Rule" rows render "—".
 *
 * Decorates an existing OI snapshot only — never creates one, same rule as
 * `remoteIdToOiUpdate`, or a drone with no declared volume would sprout a
 * ghost OI record.
 */
export function outputTelemetryToOiUpdate(raw: unknown): OutputTelemetryOiUpdate | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const droneId = asText(firstOf(p, 'aircraft_serial', 'sn', 'droneId'));
  if (!droneId) return null;

  const partial: OutputTelemetryOiUpdate['partial'] = {};
  const opType = asText(p.mission_main_flight_type);
  const legal = asText(p.legal_profile);
  const notes = asText(p.mission_notes);
  if (opType) partial.op_type = opType;
  if (legal) partial.legal_rule = legal;
  if (notes) partial.mission_notes = notes;

  // OI volume band — this topic is the only publisher of one that declares its
  // own datum. See lib/mqtt/oiAltitude.ts for why waypoints must never fill it.
  const band = readOiAltitudeBand(p);
  if (band.floorM != null || band.ceilingM != null) {
    partial.altitude_floor_m = band.floorM;
    partial.altitude_ceiling_m = band.ceilingM;
    partial.altitude_datum = band.datum;
  }

  return Object.keys(partial).length > 0 ? { drone_id: droneId, partial } : null;
}

export interface OutputC2Update {
  drone_id: string;
  /** Overview → Electronic Conspicuity → TIS-B. Replaces the dead
   *  `TISB Stream/{sn}` topic. */
  tisb_status: 'Active' | 'N/A';
  /** Overview → Aircraft Agent tile. `false` is what raises the
   *  `adapter_disconnected` watchdog event backend-side. */
  adapter_connected: boolean | null;
}

export function outputTelemetryToC2Update(raw: unknown): OutputC2Update | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const droneId = asText(firstOf(p, 'aircraft_serial', 'sn', 'droneId'));
  if (!droneId) return null;

  // Two flags: `share_telemetry_with_TISB` is the operator's opt-in,
  // `tisb_rebroadcast_enabled` is whether the service is actually
  // rebroadcasting. Only the second means "Active" — SIMTL80 publishes
  // opt-in 1 with rebroadcast false, so conflating them would show a
  // conspicuity status the aircraft doesn't have.
  const rebroadcast = asBool(p.tisb_rebroadcast_enabled);
  if (rebroadcast == null && p.adapter_connected === undefined) return null;

  return {
    drone_id: droneId,
    tisb_status: rebroadcast === true ? 'Active' : 'N/A',
    adapter_connected: asBool(p.adapter_connected),
  };
}
